import React from 'react';

interface SideNavigationProps {
  activeView: 'calendar' | 'queue' | 'analytics' | 'media' | 'campaigns' | 'accounts';
  onChangeView: (view: 'calendar' | 'queue' | 'analytics' | 'media' | 'campaigns' | 'accounts') => void;
  onCreatePost: () => void;
}

/**
 * Side Navigation Component
 * Provides navigation between different views of the Social Media Planner
 */
const SideNavigation: React.FC<SideNavigationProps> = ({
  activeView,
  onChangeView,
  onCreatePost
}) => {
  const navItems = [
    { 
      id: 'calendar', 
      label: 'Calendar', 
      icon: (
        <div className="w-5 h-5 flex items-center justify-center">
          <div className="border border-current w-4 h-4 rounded-sm flex flex-col">
            <div className="h-1 bg-current mx-auto w-3 mt-0.5"></div>
            <div className="text-[8px] font-bold mt-px">31</div>
          </div>
        </div>
      ) 
    },
    { 
      id: 'queue', 
      label: 'Queue', 
      icon: (
        <div className="w-5 h-5 flex flex-col justify-center space-y-0.5">
          <div className="h-0.5 w-4 bg-current rounded-full"></div>
          <div className="h-0.5 w-3 bg-current rounded-full"></div>
          <div className="h-0.5 w-4 bg-current rounded-full"></div>
        </div>
      ) 
    },
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: (
        <div className="w-5 h-5 flex items-end justify-center space-x-0.5">
          <div className="h-2 w-1 bg-current rounded-t"></div>
          <div className="h-3 w-1 bg-current rounded-t"></div>
          <div className="h-4 w-1 bg-current rounded-t"></div>
        </div>
      ) 
    },
    { 
      id: 'media', 
      label: 'Media', 
      icon: (
        <div className="w-5 h-5 flex items-center justify-center">
          <div className="border border-current w-4 h-4 rounded-sm relative">
            <div className="absolute top-0.5 left-0.5 w-1 h-1 rounded-full bg-current"></div>
            <div className="absolute bottom-0 right-0 w-2 h-2 border-l border-t border-current"></div>
          </div>
        </div>
      ) 
    },
    { 
      id: 'campaigns', 
      label: 'Campaigns', 
      icon: (
        <div className="w-5 h-5 flex items-center justify-center">
          <div className="border border-current w-4 h-4 rounded-sm flex flex-col items-center justify-center">
            <div className="w-3 h-3 flex flex-col">
              <div className="h-0.5 w-3 bg-current rounded-full mb-0.5"></div>
              <div className="h-0.5 w-2 bg-current rounded-full mb-0.5"></div>
              <div className="h-0.5 w-3 bg-current rounded-full"></div>
            </div>
          </div>
        </div>
      ) 
    },
    { 
      id: 'accounts', 
      label: 'Accounts', 
      icon: (
        <div className="w-5 h-5 flex items-center justify-center">
          <div className="border border-current w-4 h-4 rounded-sm flex flex-col items-center justify-center">
            <div className="w-3 h-3 flex items-center justify-center">
              <div className="w-2.5 h-2.5 rounded-full border border-current flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full border-t border-current"></div>
              </div>
            </div>
          </div>
        </div>
      ) 
    },
  ];

  return (
    <div className="w-64 h-full bg-white shadow-md flex flex-col">
      {/* Logo/Branding */}
      <div className="p-6 border-b">
        <h2 className="text-xl font-bold text-blue-600">Social Media</h2>
        <p className="text-sm text-gray-600">Planner & Scheduler</p>
      </div>

      {/* New Post Button */}
      <div className="p-4">
        <button 
          onClick={onCreatePost}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md flex items-center justify-center"
        >
          <div className="w-5 h-5 flex items-center justify-center mr-2">
            <div className="relative w-4 h-4">
              <div className="absolute inset-y-1.5 w-4 h-1 bg-current rounded-full"></div>
              <div className="absolute inset-x-1.5 w-1 h-4 bg-current rounded-full"></div>
            </div>
          </div>
          New Post
        </button>
      </div>

      {/* Navigation Menu */}
      <div className="mt-6 flex-1">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                className={`flex items-center w-full px-6 py-3 text-left ${
                  activeView === item.id
                    ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => onChangeView(item.id as any)}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer */}
      <div className="p-4 mt-auto border-t">
        <div className="px-6 py-2">
          <p className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Explanatum
          </p>
        </div>
      </div>
    </div>
  );
};

export default SideNavigation;
